import api from './api';

/**
 * Current Fuel Index — read-only client for the V1 fuelcharge.pl port.
 *
 * V1 ships two views over the same fueldata.fuelindex table. 360 shows them as
 * ONE table with grouped headers (Customer FSC / Driver), so the two endpoints
 * are merged on beginDate by mergeFuelIndexRows() below rather than by a third
 * combined endpoint — same rows, same source table, and no extra endpoint to
 * register and grant. Both return the full ~956-week history, newest first.
 */

/** One week as `/fuel-index/customer` returns it — the customer-facing FSC. */
export interface CustomerFuelRow {
  /** ISO yyyy-mm-dd, for sorting and date filters. */
  beginDate: string | null;
  /** V1 "Week of", pre-formatted MM/DD/YYYY. */
  weekOf: string;
  /** V1 "Effective", the Tue..Mon window this reading applies to, e.g. "08/18 - 08/24".
   *  Customer-FSC only — V1 never shows it on the driver view. */
  effective: string;
  nationalIndex: number | null;
  gulfIndex: number | null;
  midwestIndex?: number | null;
  eastCoastIndex?: number | null;
  rockyMountainIndex?: number | null;
  westCoastIndex?: number | null;
  newEnglandIndex?: number | null;
  centralAtlanticIndex?: number | null;
  lowerAtlanticIndex?: number | null;
  westCoastLessCaliforniaIndex?: number | null;

  /** fsc_customer.drayotr — OTR is the FSC (DS2-3495). */
  fsc: number | null;
}

/** One week as `/fuel-index/driver` returns it — the driver pay rates. */
export interface DriverFuelRow {
  beginDate: string | null;
  weekOf: string;
  nationalIndex: number | null;
  gulfIndex: number | null;
  midwestIndex?: number | null;
  eastCoastIndex?: number | null;
  rockyMountainIndex?: number | null;
  westCoastIndex?: number | null;
  newEnglandIndex?: number | null;
  centralAtlanticIndex?: number | null;
  lowerAtlanticIndex?: number | null;
  westCoastLessCaliforniaIndex?: number | null;

  gulfLocal: number | null;
  gulfOtr: number | null;
  southeastOtr: number | null;
}

/** One week with both sides joined — the shape the combined table renders. */
export interface FuelIndexRow {
  beginDate: string | null;
  weekOf: string;
  nationalIndex: number | null;
  gulfIndex: number | null;
  midwestIndex?: number | null;
  eastCoastIndex?: number | null;
  rockyMountainIndex?: number | null;
  westCoastIndex?: number | null;
  newEnglandIndex?: number | null;
  centralAtlanticIndex?: number | null;
  lowerAtlanticIndex?: number | null;
  westCoastLessCaliforniaIndex?: number | null;

  // Customer FSC group
  effective: string;
  fsc: number | null;
  // Driver group
  gulfLocal: number | null;
  gulfOtr: number | null;
  southeastOtr: number | null;
}

/**
 * Join the customer and driver rows on beginDate.
 *
 * Both endpoints read the same fuelindex table with no WHERE clause, so in
 * practice the two lists are the same weeks in the same order. The join is
 * still done by key rather than by index so a mismatch degrades to blank cells
 * instead of silently pairing the wrong week's rates to a week's FSC. Customer
 * ordering (recno DESC) is preserved, and a driver-only week is appended so no
 * reading disappears.
 */
export const mergeFuelIndexRows = (
  customer: CustomerFuelRow[],
  driver: DriverFuelRow[]
): FuelIndexRow[] => {
  const driverByDate = new Map<string, DriverFuelRow>();
  for (const d of driver) {
    if (d.beginDate) driverByDate.set(d.beginDate, d);
  }

  const merged: FuelIndexRow[] = customer.map((c) => {
    const d = c.beginDate ? driverByDate.get(c.beginDate) : undefined;
    if (c.beginDate) driverByDate.delete(c.beginDate);
    return {
      beginDate: c.beginDate,
      weekOf: c.weekOf,
      nationalIndex: c.nationalIndex,
      gulfIndex: c.gulfIndex,
      midwestIndex: c.midwestIndex ?? d?.midwestIndex ?? null,
      eastCoastIndex: c.eastCoastIndex ?? d?.eastCoastIndex ?? null,
      rockyMountainIndex: c.rockyMountainIndex ?? d?.rockyMountainIndex ?? null,
      westCoastIndex: c.westCoastIndex ?? d?.westCoastIndex ?? null,
      newEnglandIndex: c.newEnglandIndex ?? d?.newEnglandIndex ?? null,
      centralAtlanticIndex: c.centralAtlanticIndex ?? d?.centralAtlanticIndex ?? null,
      lowerAtlanticIndex: c.lowerAtlanticIndex ?? d?.lowerAtlanticIndex ?? null,
      westCoastLessCaliforniaIndex: c.westCoastLessCaliforniaIndex ?? d?.westCoastLessCaliforniaIndex ?? null,

      effective: c.effective,
      fsc: c.fsc,
      gulfLocal: d?.gulfLocal ?? null,
      gulfOtr: d?.gulfOtr ?? null,
      southeastOtr: d?.southeastOtr ?? null,
    };
  });

  // Any driver week with no customer counterpart still gets a row.
  for (const d of driverByDate.values()) {
    merged.push({
      beginDate: d.beginDate,
      weekOf: d.weekOf,
      nationalIndex: d.nationalIndex,
      gulfIndex: d.gulfIndex,
      midwestIndex: d.midwestIndex ?? null,
      eastCoastIndex: d.eastCoastIndex ?? null,
      rockyMountainIndex: d.rockyMountainIndex ?? null,
      westCoastIndex: d.westCoastIndex ?? null,
      newEnglandIndex: d.newEnglandIndex ?? null,
      centralAtlanticIndex: d.centralAtlanticIndex ?? null,
      lowerAtlanticIndex: d.lowerAtlanticIndex ?? null,
      westCoastLessCaliforniaIndex: d.westCoastLessCaliforniaIndex ?? null,

      effective: '',
      fsc: null,
      gulfLocal: d.gulfLocal,
      gulfOtr: d.gulfOtr,
      southeastOtr: d.southeastOtr,
    });
  }

  return merged;
};

const base = '/fuel-index';

const fuelIndexService = {
  async getCustomerFuelIndex(signal?: AbortSignal): Promise<CustomerFuelRow[]> {
    const res = await api.get(`${base}/customer`, { signal });
    return (res.data?.rows ?? []) as CustomerFuelRow[];
  },

  async getDriverFuelRates(signal?: AbortSignal): Promise<DriverFuelRow[]> {
    const res = await api.get(`${base}/driver`, { signal });
    return (res.data?.rows ?? []) as DriverFuelRow[];
  },
};

export default fuelIndexService;
